import moment from "moment";
import React, {
  useState,
  useEffect,
  PropsWithChildren,
  CSSProperties,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { ChartData } from "../components/ChartData";
import { useChart } from "../contexts/ChartDataContext";
import { SpinnyThingy } from "../components/Misc/SpinnyThingy";
import {
  DeviceNode,
  ChartTypes,
  SensorData,
  SensorColours,
  Materials,
} from "../types/types";
import { TSensorTypes } from "../types/generated_types";
import { Requester } from "../utils/Requester";
import { calculateMCValue } from "../utils/utility";
import {
  exportQueryDataFromNodes,
  filterQueryDataByChartlines,
} from "./DisplayChart.helper";
import { ReportConfigNodes } from "../types/types";

interface Props {
  nodes: Array<DeviceNode> | Array<ReportConfigNodes>;
  noNodesAvailable: boolean;
  selSensorTypes: Array<TSensorTypes>;
  secondaryAxis: TSensorTypes | undefined;
  startDate: Date;
  endDate: Date;
  chartType: ChartTypes;
  forcedTimezone: string;
  style?: CSSProperties;
  loading: boolean;
  setLoading: (arg: boolean) => void;
  yMaxMain?: number;
  yMinMain?: number;
  yMaxSecond?: number;
  yMinSecond?: number;
  mcList?: Map<string, Materials>;
  isThisReport?: boolean;
}

interface ProcessedSensorData extends SensorData {
  dt: Date;
}

interface PrefetchedDataCache {
  // Map `distinct_chart_line` -> ProcessedSensorData[]
  data: Map<string, ProcessedSensorData[]>;
  // The "invariants" -- params this was fetched for!
  // If changed, we need to do full re-fetch
  cachedChartType: ChartTypes;
  cachedTimeRange: {x1: Date, x2: Date};
}

export function DisplayChart({
  nodes,
  noNodesAvailable,
  selSensorTypes,
  secondaryAxis,
  startDate,
  endDate,
  chartType,
  style,
  forcedTimezone,
  loading,
  yMaxMain,
  yMinMain,
  yMaxSecond,
  yMinSecond,
  mcList,
  setLoading,
  isThisReport,
}: PropsWithChildren<Props>): React.ReactElement {
  /* Data storage */
  // prefetchedData: we store fetched data here (can be incremental)
  const [prefetchedData, setPrefetchedData] = useState<PrefetchedDataCache>();
  // sortedData: data selected for display, which we give to the <ChartData>
  //             same or subset of prefetched data
  const [sortedData, setSortedData] = useState<Array<ProcessedSensorData>>([]);
  // wmeData: WME data _only_ when needed for MC calculation
  const [wmeData, setWmeData] = useState<Array<ProcessedSensorData>>([]);
  // mcData: Data we calculate client-side from WME and piggy-back to
  //         sensorData, whenever any MC is selected
  const [mcData, setMCData] = useState<Array<ProcessedSensorData>>([]);

  const chart = useChart();
  const { t } = useTranslation();

  const colours = useMemo(() => {
    const colorsNew: SensorColours = {};
    nodes.forEach((node) => {
      node.fields.forEach((sens) => {
        colorsNew[sens.front_end_name.toString()] = sens.sensor_color;
      });
    });
    return colorsNew;
  }, [nodes]);

  /* Data fetch */
  useEffect(() => {
    // Is this going to be a full or a partial fetch?
    const no_data_yet = (prefetchedData === undefined);
    const invariants_changed = (
      prefetchedData?.cachedChartType !== chartType ||
      prefetchedData?.cachedTimeRange.x1 !== startDate ||
      prefetchedData?.cachedTimeRange.x2 !== endDate
    );
    const full_fetch = (no_data_yet || invariants_changed);

    /* Pre-process parameters */
    // NOTE: Similar to below, but only the pre-fetch actions
    // If we have MC selected, it should imply WME so we have source data
    // to calculate the MC from.
    const getParamNodes = exportQueryDataFromNodes(nodes);
    let processedParams = getParamNodes.map((n) => {
      if (n.types.includes("MC")) {
        const newTypes = n.types.filter((e) => e !== "MC")  // Filter out MC
        if (!newTypes.includes("WME"))
          newTypes.push("WME")  // Add WME if it wasn't asked already
        return {...n, types: newTypes}
      }
      return n;  // No processing if there's no MC
    });
    // for partial fetch, filter out anything we already have
    if (!full_fetch) {
      processedParams = filterQueryDataByChartlines(
        // NOTE: If we could declare `distinct_chart_lines` as ReadonlySetLike
        //       we could pass `prefetched.data` directly, because Map _is_ set-like.
        //       May need to wait on TypeScript 5 for that. :)
        processedParams, new Set(prefetchedData.data.keys())
      );
    }

    if (processedParams.length > 0) {
      const getParams = {
        chartType,
        x1: startDate,
        x2: endDate,
        nodes: JSON.stringify(processedParams),
      };
      setLoading(true);
      void Requester.getChartDataCall(getParams)
        .then((data) => {
          const adjustedData = data.data.map((e) => {
            return {...e, dt: new Date(e.captured_datetime)};
          })
          .map((e) => {
            // Shift the timezone for sensor data
            e.captured_datetime = moment(e.captured_datetime)
              .tz(forcedTimezone)
              .format("YYYY-MM-DD HH:mm:ss");
            return e;
          });

          // Split data by their `distinct_chart_line` attibute
          // FIXME: Or maybe have back-end do this for us?
          // NOTE: Take from `processedParams` as some sensors may not have
          //       any data for selected range (or at all)
          const selectedChartLines = processedParams.flatMap((n) =>
            n.types.map((type) => `${type}_${n.id}`)
          );
          // Combine with existing data (if doing partial fetch)
          let newdata = new Map(
            (full_fetch)? undefined : prefetchedData.data
          )
          // Populate!
          selectedChartLines.forEach((chartLine) => {
            newdata.set(chartLine, adjustedData.filter(
              (e) => e.distinct_chart_line === chartLine
            ))
          })

          // Update the prefetch
          setPrefetchedData({
            data: newdata,
            cachedChartType: chartType,
            cachedTimeRange: {x1: startDate, x2: endDate},
          })
        })
        .then(() => setLoading(false));
    } else if (noNodesAvailable) {
      setLoading(false);
    }
  }, [ // deps
    chartType,
    startDate,
    endDate,
    forcedTimezone,
    nodes,
    noNodesAvailable,
    prefetchedData,
    setLoading,
  ]);

  /* Post-fetch selection */
  useEffect(() => {
    if (!prefetchedData)
      return;  // No data? Bail out early!

    /* Pre-process parameters */
    // NOTE: Similar to above, but only the post-fetch actions
    // Mark WME "needed" if MC is selected to channel it to `wmeData`
    const getParamNodes = exportQueryDataFromNodes(nodes);
    let neededforMC: string[] = [];  // include *only* these in `wmeData`
    for (const n of getParamNodes) {
      if (!n.types.includes("MC"))
        continue;  // No processing if there's no MC
      neededforMC.push(`WME_${n.id}`);  // mark WME as "needed"
    }

    const selectedChartLines = getParamNodes.flatMap((n) =>
      // NOTE: selections may include MC which is _never_ in
      //       the `prefetchedData`, but selecting them is a harmless no-op
      n.types.map((type) => `${type}_${n.id}`)
    );

    // Set data for charting
    let newSortedData = Array<ProcessedSensorData>();
    for (const i of selectedChartLines) {
      const piece = prefetchedData.data.get(i);
      if (piece)
        newSortedData = newSortedData.concat(piece)
    }
    setSortedData(newSortedData);

    // Separate out data for MC calculation
    let newWmeData = Array<ProcessedSensorData>();
    for (const i of neededforMC) {
      const piece = prefetchedData.data.get(i);
      if (piece)
        newWmeData = newWmeData.concat(piece)
    }
    setWmeData(newWmeData);
  }, [nodes, prefetchedData]);

  useEffect(() => {
    if (!mcList) return;
    let newMCData = Array<ProcessedSensorData>();
    // Chart color data uses node primary key, this will be used to look them up
    const nodeIdMap = new Map();
    for (const node of nodes) {
      nodeIdMap.set(node.local_id, node.id);
    }
    for (let data of wmeData) {
      const material = mcList.get(data.node_id);
      if (material) {
        newMCData.push({
          ...data,
          distinct_chart_line: `MC_${nodeIdMap.get(data.node_id)}`,
          sensor_type: TSensorTypes.MC,
          value: calculateMCValue(parseFloat(data.value), material).toString(),
        })
      }
    }
    setMCData(newMCData);
  }, [mcList, nodes, wmeData, setMCData]);

  const loadAllData = () => {
    chart.loadDataRange(
      chart.availableDataRange.start,
      chart.availableDataRange.end,
      true,
      false,
    );
  }

  useEffect(() => {
    if (!isThisReport) {
      chart.setHasData(sortedData.length > 0 || mcData.length > 0);
    }
  }, [sortedData, mcData, chart, isThisReport]);

  return (
    <ChartData
      selSensorTypes={selSensorTypes}
      secondaryAxis={secondaryAxis}
      style={style}
      data={sortedData.concat(mcData).sort((a, b) => {
        return a.dt.getTime() - b.dt.getTime();
      })}
      colors={colours}
      loading={loading}
      setLoading={setLoading}
      t={t}
      yMaxMain={yMaxMain}
      yMinMain={yMinMain}
      yMaxSecond={yMaxSecond}
      yMinSecond={yMinSecond}
      loadAllData={loadAllData}
      isThisReport={isThisReport}
    >
      <div className="container spinny-thingy-container" style={{height:"35rem"}}>
        <SpinnyThingy />
      </div>
    </ChartData>
  );
}
